import type { TrackerConfig } from './contracts';
import type { TrackerLogger } from './logger';

const OPT_OUT_KEY = 'uplane_tracking_disabled';

const storageKey = (kind: 'visitor' | 'session', workspaceId: string): string =>
  `uplane_${kind}_${workspaceId}`;

const generateId = (): string => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const readStorage = (storage: Storage, key: string, logger: TrackerLogger): string | null => {
  try {
    return storage.getItem(key);
  } catch (error) {
    logger.debug('storage_read', { key, outcome: 'unavailable', error: error instanceof Error ? error.message : String(error) });
    return null;
  }
};

const writeStorage = (storage: Storage, key: string, value: string, logger: TrackerLogger): boolean => {
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    logger.debug('storage_write', { key, outcome: 'unavailable', error: error instanceof Error ? error.message : String(error) });
    return false;
  }
};

const removeStorage = (storage: Storage, key: string, logger: TrackerLogger): void => {
  try {
    storage.removeItem(key);
  } catch (error) {
    logger.debug('storage_remove', { key, outcome: 'unavailable', error: error instanceof Error ? error.message : String(error) });
  }
};

const getStorage = (kind: 'localStorage' | 'sessionStorage', logger: TrackerLogger): Storage | null => {
  try {
    return window[kind];
  } catch (error) {
    logger.debug('storage_access', {
      storage: kind,
      outcome: 'unavailable',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

export type StoredSession = {
  id: string;
  startedAt: number;
  lastActivityAt: number;
  activeMs: number;
  /**
   * The referrer of the page that started the session; null when it had
   * none. Undefined when it is not known: a session continued from a linked
   * domain or stored by an older SDK.
   */
  referrer?: string | null;
};

const parseSession = (value: string | null, logger: TrackerLogger): StoredSession | null => {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'id' in parsed &&
      'lastActivityAt' in parsed &&
      typeof parsed.id === 'string' &&
      typeof parsed.lastActivityAt === 'number' &&
      Number.isFinite(parsed.lastActivityAt)
    ) {
      const startedAt = 'startedAt' in parsed && typeof parsed.startedAt === 'number' && Number.isFinite(parsed.startedAt)
        ? parsed.startedAt
        : parsed.lastActivityAt;
      const activeMs = 'activeMs' in parsed && typeof parsed.activeMs === 'number' && Number.isFinite(parsed.activeMs)
        ? Math.max(0, parsed.activeMs)
        : 0;
      const referrer = 'referrer' in parsed && (typeof parsed.referrer === 'string' || parsed.referrer === null)
        ? { referrer: parsed.referrer }
        : {};
      return { id: parsed.id, startedAt, lastActivityAt: parsed.lastActivityAt, activeMs, ...referrer };
    }
  } catch (error) {
    logger.debug('parse_session', {
      outcome: 'invalid',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
  return null;
};

/**
 * Keeps the persistent ids. The session lives in localStorage, so every tab
 * of the browser continues one session until it times out, as a visitor who
 * opens a link in a new tab expects.
 */
export class IdentityStore {
  private readonly local: Storage | null;
  private readonly visitorKey: string;
  private readonly sessionKey: string;
  private memoryVisitorId: string | null = null;
  private memorySession: StoredSession | null = null;

  constructor(
    private readonly config: TrackerConfig,
    private readonly logger: TrackerLogger
  ) {
    this.local = config.privacyMode === 'persistent' ? getStorage('localStorage', logger) : null;
    this.visitorKey = storageKey('visitor', config.workspaceId);
    this.sessionKey = storageKey('session', config.workspaceId);
  }

  getVisitorId(): string {
    const stored = this.local ? readStorage(this.local, this.visitorKey, this.logger) : null;
    if (stored) return stored;
    if (this.memoryVisitorId) return this.memoryVisitorId;

    const visitorId = generateId();
    this.memoryVisitorId = visitorId;
    if (this.local) writeStorage(this.local, this.visitorKey, visitorId, this.logger);
    return visitorId;
  }

  getSession(now: number, referrer: string | null): StoredSession {
    const record = this.readSharedSession() ?? this.memorySession;

    if (record && now - record.lastActivityAt < this.config.sessionTimeoutMs) {
      return { ...record };
    }

    return this.createSession(now, referrer);
  }

  /** The session that the tabs of this browser share; null in anonymous mode or without storage. */
  readSharedSession(): StoredSession | null {
    return this.local ? parseSession(readStorage(this.local, this.sessionKey, this.logger), this.logger) : null;
  }

  createSession(now: number, referrer: string | null): StoredSession {
    const record = { id: generateId(), startedAt: now, lastActivityAt: now, activeMs: 0, referrer };
    this.saveSession(record);
    return record;
  }

  /** Continues the ids of a linked page. The session counters start again on this page. */
  adopt(visitorId: string, sessionId: string, now: number): void {
    this.memoryVisitorId = visitorId;
    if (this.local) writeStorage(this.local, this.visitorKey, visitorId, this.logger);
    this.saveSession({ id: sessionId, startedAt: now, lastActivityAt: now, activeMs: 0 });
  }

  saveSession(record: StoredSession): void {
    this.memorySession = record;
    if (this.local) writeStorage(this.local, this.sessionKey, JSON.stringify(record), this.logger);
  }

  static isOptedOut(logger: TrackerLogger): boolean {
    const local = getStorage('localStorage', logger);
    return local ? readStorage(local, OPT_OUT_KEY, logger) === 'true' : false;
  }

  static setOptOut(optedOut: boolean, logger: TrackerLogger): void {
    const local = getStorage('localStorage', logger);
    if (!local) return;
    if (optedOut) writeStorage(local, OPT_OUT_KEY, 'true', logger);
    else removeStorage(local, OPT_OUT_KEY, logger);
  }

  static clearTrackingIds(workspaceId: string, logger: TrackerLogger): void {
    const local = getStorage('localStorage', logger);
    const session = getStorage('sessionStorage', logger);
    if (local) removeStorage(local, storageKey('visitor', workspaceId), logger);
    if (local) removeStorage(local, storageKey('session', workspaceId), logger);
    // SDK versions before 2.6.0 kept the session in sessionStorage.
    if (session) removeStorage(session, storageKey('session', workspaceId), logger);
  }
}
